import React, { useState, useEffect, useContext } from 'react'
/** @jsx jsx */
import { jsx } from '@emotion/core'
import styled from '@emotion/styled'
import { Modal, Select, message, Button } from 'antd'
import R from 'ramda'
import moment from 'moment-timezone'
import { ErrorContext, SpinContext } from 'context'
import { getNonHydroEvents, getLastNonHydroEvent } from 'api/pgads'

const minutes = R.range(0, 60)
const hrs = R.range(0, 24)

const GridHeader = styled.span`
  font-weight: 650;
  text-align: center;
`

const tzNames = {
  CST: 'America/Chicago',
  PST: 'America/Los_Angeles',
  EST: 'America/New_York',
  MST: 'America/Denver',
}

const InsertItem = ({
  dataIndex,
  date = '',
  hour,
  min,
  zone,
  unitZone,
  unitDlsZone,
  unitDlsEnabled,
  status,
  setDataItems,
  disabled,
  generationOptionEndChange,
  withDelete = true,
  deleted = false,
  key,
  id,
}) => {
  // if (deleted) return <React.Fragment />
  // else
  return (
    <div
      css={{
        display: 'grid',
        gridGap: '10px',
        gridTemplateColumns: '100px 100px 100px 100px 150px 100px',
        backgroundColor: disabled ? 'lightgray' : 'inherit',
      }}
      id={id}
      key={key}
    >
      <span>{date}</span>
      <Select
        id="hour"
        value={hour}
        onChange={value => setDataItems(dataIndex, 'hour', value)}
        disabled={deleted}
      >
        {hrs.map(hr => (
          <Select.Option key={hr} value={hr} disabled={disabled}>
            {String(hr).padLeft(2, '0')}
          </Select.Option>
        ))}
      </Select>
      <Select
        id="min"
        value={min}
        onChange={value => setDataItems(dataIndex, 'min', value)}
        disabled={disabled || deleted}
      >
        {minutes.map(m => (
          <Select.Option key={m} value={m}>
            {String(m).padLeft(2, '0')}
          </Select.Option>
        ))}
      </Select>
      <Select
        id="zone"
        value={zone}
        onChange={value => setDataItems(dataIndex, 'zone', value)}
        disabled={disabled || deleted}
      >
        <Select.Option value={unitZone}>{unitZone}</Select.Option>
        {unitDlsEnabled === 'Y' && (
          <Select.Option value={unitDlsZone}>{unitDlsZone}</Select.Option>
        )}
      </Select>
      <Select
        id="status"
        value={status}
        onChange={value => setDataItems(dataIndex, 'status', value)}
        disabled={disabled || deleted}
      >
        <Select.Option key="Generation Start">Generation Start</Select.Option>
        {generationOptionEndChange && (
          <Select.Option key="Generation End">Generation End</Select.Option>
        )}
        {!generationOptionEndChange && (
          <Select.Option key="Generation Stop">Generation Stop</Select.Option>
        )}
      </Select>
      {withDelete && (
        <Button
          id="delete"
          onClick={_ => setDataItems(dataIndex, 'deleted', true)}
          type="danger"
          icon="delete"
          style={{ justifySelf: 'center' }}
          disabled={deleted}
        />
      )}
    </div>
  )
}

const NonHydroEvenInsertModal = ({
  visible,
  apiTargetInfo,
  unit,
  utility,
  unitName,
  date,
  unitZone,
  unitDlsEnabled,
  onOk,
  onCancel,
  generationOptionEndChange,
  spinning = false,
}) => {
  const unitDlsZone = `${unitZone[0]}D${unitZone[2]}`
  const { setSpinning } = useContext(SpinContext)
  const { setError } = useContext(ErrorContext)
  const [dataStates, setDataStates] = useState([])
  const [lastEvent, setLastEvent] = useState({})

  useEffect(() => {
    setSpinning(true)
    getNonHydroEvents(apiTargetInfo)({
      transDate: date,
      unitIds: [`${utility}${unit}`],
    }).fork(setError, data => {
      setDataStates(
        data.map(d => ({
          hour: parseInt(d.zoneDateString.split(' ')[1].split(':')[0]),
          min: parseInt(d.zoneDateString.split(' ')[1].split(':')[1]),
          zone: d.zone,
          status: d.status,
        }))
      )
    })
    getLastNonHydroEvent(apiTargetInfo)({
      transDate: date,
      unit,
      utility,
    }).fork(setError, data => {
      const modifiedData = data.map(d => ({
        date: moment(d.zoneDateString, 'MM/DD/YYYY').format('YYYY-MM-DD'),
        hour: parseInt(d.zoneDateString.split(' ')[1].split(':')[0]),
        min: parseInt(d.zoneDateString.split(' ')[1].split(':')[1]),
        zone: d.zone,
        status: d.status,
      }))
      setLastEvent(modifiedData[0] || {})
      setSpinning(false)
    })
  }, [apiTargetInfo, date, setError, setSpinning, unit, utility])

  const setDataItems = (index, type, value) => {
    const ds = dataStates
    let d = ds[index]
    d = {
      zone:
        unitDlsEnabled === 'Y' && moment.tz(date, tzNames[unitZone]).isDST()
          ? unitDlsZone
          : unitZone,
      ...d,
      [type]: value,
      updated: true,
    }
    ds[index] = d
    setDataStates([...ds])
  }

  // const hideDeleteDataItems = dataItems => dataItems.filter(d => !d.deleted)

  const onOkData = _ => {
    const rowIsInvalid = d => {
      if (
        (!moment(`${date} ${d.hour}:${d.min}`, 'YYYY-MM-DD H:m').isValid() ||
          !d.zone ||
          !d.status) &&
        !(d.updated && d.deleted)
      ) {
        return true
      } else {
        return false
      }
    }
    if (dataStates.some(rowIsInvalid)) {
      message.error('Please fill out all the fields')
      return
    }

    const data = dataStates.map(d => ({
      ...d,
      unit,
      utility,
      unitZone,
      transactionDate: moment(
        `${date} ${d.hour}:${d.min}`,
        'YYYY-MM-DD H:m'
      ).format('YYYY-MM-DD HH:mm'),
    }))
    onOk(data)
  }
  const getDate = (data, date, curIdx) => {
    const first = data.findIndex(d => !d.deleted)
    if (first === curIdx) return date
    else return ''
  }

  return (
    <Modal
      width={800}
      height={700}
      title={`Non Hydro Operation - ${unitName}  - ${unitZone} - ${date}`}
      visible={visible}
      onOk={onOkData}
      onCancel={onCancel}
      zIndex={999}
      top={10}
      style={{ top: 10 }}
      okButtonProps={{ loading: spinning }}
      cancelButtonProps={{ loading: spinning }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr',
          overflowY: 'auto',
          maxHeight: '500px',
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr',
            gridGap: '10px',
          }}
        >
          <div
            css={{
              display: 'grid',
              gridGap: '10px',
              gridTemplateColumns: '100px 100px 100px 100px 150px 100px',
            }}
          >
            <GridHeader>Date</GridHeader>
            <GridHeader>Hour</GridHeader>
            <GridHeader>Minutes</GridHeader>
            <GridHeader>Timezone</GridHeader>
            <GridHeader>Status</GridHeader>
            <GridHeader>Delete</GridHeader>
          </div>
          <InsertItem
            key="disabled"
            {...lastEvent}
            id="disabled"
            {...lastEvent}
            unitZone={unitZone}
            unitDlsZone={unitDlsZone}
            unitDlsEnabled={unitDlsEnabled}
            disabled
            generationOptionEndChange={generationOptionEndChange}
            withDelete={false}
          />
          {dataStates.map((ds, i) => {
            if (ds.deleted) return <React.Fragment key={i} />
            else
              return (
                <InsertItem
                  key={i}
                  id={i}
                  dataIndex={i}
                  // date={i === 0 ? date : ''}
                  date={getDate(dataStates, date, i)}
                  unitZone={unitZone}
                  unitDlsZone={unitDlsZone}
                  unitDlsEnabled={unitDlsEnabled}
                  {...ds}
                  setDataItems={setDataItems}
                  generationOptionEndChange={generationOptionEndChange}
                />
              )
          })}
          <InsertItem
            key={dataStates.length}
            id={dataStates.length}
            dataIndex={dataStates.length}
            unitZone={unitZone}
            unitDlsZone={unitDlsZone}
            unitDlsEnabled={unitDlsEnabled}
            setDataItems={setDataItems}
            generationOptionEndChange={generationOptionEndChange}
            withDelete={false}
          />
        </div>
      </div>
    </Modal>
  )
}

export default NonHydroEvenInsertModal
